import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Kurzer Klick zum laufenden Zaehler beim Aufdecken.
 *
 * Der Zaehler springt in ungleichen Abstaenden und wird zum Ende hin
 * deutlich langsamer. Sichtbar ist das nur, wenn man hinschaut --
 * hoerbar wirkt es auch nebenbei, und genau darum geht es beim
 * Aufdecken.
 *
 * Bewusst per WebAudio erzeugt und nicht als Datei:
 *
 *  - Keine zusaetzliche Anfrage, kein Ladezeitpunkt, der genau im
 *    spannenden Moment noch nicht fertig ist.
 *  - Die Toenhoehe kann mit dem Fortschritt steigen. Ein Klick, der
 *    sich nach oben schraubt, traegt die Spannung von selbst.
 */

const ENABLED_KEY = 'hitster.tick';

/**
 * Sehr leise -- und relativ zur Musik.
 *
 * Der Klick liegt UEBER dem laufenden Song. Alles, was man bewusst
 * hoert, stoert ihn; er soll nur spuerbar sein.
 *
 * Der Wert ist ein ANTEIL der eingestellten Lautstaerke, kein fester
 * Pegel. Fest waere er falsch herum: wer die Musik auf 10 % dreht,
 * bekaeme einen Tick, der lauter ist als der Song.
 */
const TICK_GAIN_RATIO = 0.06;

/** Unter diesem Pegel klingt der Klick nur noch nach Knacken. */
const MIN_AUDIBLE_GAIN = 0.0005;

/** Laenge eines Klicks. Laenger klingt nach Ton, nicht nach Tick. */
const TICK_SECONDS = 0.035;

function readStoredEnabled(): boolean {
  try {
    // Standard AN: die Wirkung ist der Grund, warum es das gibt.
    return localStorage.getItem(ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

export interface TickSound {
  /**
   * Spielt einen Klick.
   *
   * `progress` (0..1) hebt die Tonhoehe zum Ende hin an. Ohne Angabe
   * bleibt sie gleich.
   */
  tick: (progress?: number) => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

/**
 * @param volume Lautstaerke der Musik (0..1). Der Klick richtet sich
 *   danach, damit er nie lauter wird als der Song, ueber dem er liegt.
 */
export function useTickSound(volume = 1): TickSound {
  const [enabled, setEnabledState] = useState(readStoredEnabled);
  const contextRef = useRef<AudioContext | null>(null);

  // Ueber eine Ref, damit ein Zug am Lautstaerkeregler nicht `tick`
  // neu erzeugt und damit die Animation stoert.
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    try {
      localStorage.setItem(ENABLED_KEY, next ? '1' : '0');
    } catch {
      // Privater Modus o.ae. -- die Einstellung gilt dann nur diese Sitzung.
    }
  }, []);

  /**
   * Den AudioContext erst beim ersten Klick anlegen.
   *
   * Browser starten ihn ohne vorherige Nutzergeste im Zustand
   * `suspended`. Ihn beim Laden der Seite zu erzeugen bringt also
   * nichts und wird in der Konsole bemaengelt.
   */
  const tick = useCallback(
    (progress = 0) => {
      /**
       * Die Einstellung wird HIER gelesen, nicht aus dem State.
       *
       * Der Schalter sitzt im Lautsprecher-Menue, der Ton entsteht in
       * der Aufdeck-Karte -- zwei getrennte Aufrufe des Hooks, also
       * zwei getrennte States. Ueber den React-State wuerde ein
       * Umschalten die Karte gar nicht erreichen. Ein Kontext waere
       * hier mehr Aufbau als der Zweck hergibt: ein
       * localStorage-Zugriff pro Sprung ist bei einem Dutzend
       * Spruengen pro Runde nicht messbar.
       */
      if (!readStoredEnabled()) return;

      const gain = TICK_GAIN_RATIO * Math.min(1, Math.max(0, volumeRef.current));
      // Musik stummgeschaltet -> auch kein Ticken.
      if (gain < MIN_AUDIBLE_GAIN) return;

      try {
        contextRef.current ??= new AudioContext();
        const context = contextRef.current;

        // Nach einem Tabwechsel kann er wieder angehalten sein.
        if (context.state === 'suspended') void context.resume();

        const now = context.currentTime;

        const oscillator = context.createOscillator();
        const envelope = context.createGain();

        /**
         * Dreieck statt Sinus oder Rechteck.
         *
         * Ein Sinus verschwindet hinter der Musik, ein Rechteck sticht
         * als Piepen heraus. Das Dreieck sitzt dazwischen.
         */
        oscillator.type = 'triangle';
        // Steigt ueber den Lauf um eine Oktave -- das zieht nach oben.
        oscillator.frequency.value = 880 + 880 * Math.min(1, Math.max(0, progress));

        /**
         * Sofort laut, dann exponentiell aus.
         *
         * Ohne diese Huellkurve knackt es am Anfang und am Ende, weil
         * die Wellenform hart abgeschnitten wird.
         */
        envelope.gain.setValueAtTime(gain, now);
        envelope.gain.exponentialRampToValueAtTime(MIN_AUDIBLE_GAIN / 2, now + TICK_SECONDS);

        oscillator.connect(envelope);
        envelope.connect(context.destination);

        oscillator.start(now);
        oscillator.stop(now + TICK_SECONDS);
      } catch {
        /**
         * Ton ist Beiwerk.
         *
         * Kein WebAudio, keine Erlaubnis, kein Ausgabegeraet -- das
         * Spiel laeuft weiter. Ein Fehler hier darf das Aufdecken nicht
         * abbrechen.
         */
      }
    },
    [],
  );

  // Beim Verlassen freigeben, sonst bleibt das Audiogeraet belegt.
  useEffect(() => {
    return () => {
      void contextRef.current?.close().catch(() => {});
      contextRef.current = null;
    };
  }, []);

  return { tick, enabled, setEnabled };
}
